import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface Task {
  id: string;
  organization_id: string;
  type: 'remediation' | 'evidence' | 'questionnaire';
  title: string;
  description?: string;
  source: Record<string, any>;
  assignee?: string;
  status: 'open' | 'in_progress' | 'blocked' | 'done';
  due_at?: string;
  notes?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export function useTasks(filters?: { 
  status?: string; 
  assignee?: string; 
  type?: string; 
}) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, profile } = useAuth();

  useEffect(() => {
    if (user && profile?.organization_id) {
      fetchTasks();
    }
  }, [user, profile, filters]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.functions.invoke('tasks-api', {
        body: { action: 'list' }
      });

      if (error) throw error;

      setTasks((data?.tasks || []) as Task[]);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const createTask = async (taskData: {
    type: Task['type'];
    title: string;
    description?: string;
    source?: Record<string, any>;
    assignee?: string;
    due_at?: string;
  }) => {
    try {
      const { data, error } = await supabase.functions.invoke('tasks-api', {
        body: {
          action: 'create',
          type: taskData.type,
          title: taskData.title,
          description: taskData.description,
          source: taskData.source || {},
          assignee: taskData.assignee,
          due_at: taskData.due_at
        }
      });

      if (error) throw error;
      
      await fetchTasks(); // Refresh list
      return data;
    } catch (err) {
      console.error('Error creating task:', err);
      throw err instanceof Error ? err : new Error('Failed to create task');
    }
  };

  const updateTask = async (id: string, updates: Partial<Task>) => {
    try {
      const { data, error } = await supabase.functions.invoke('tasks-api', {
        body: {
          action: 'update',
          id,
          updates
        }
      });

      if (error) throw error;

      await fetchTasks(); // Refresh list
      return data?.task;
    } catch (err) {
      console.error('Error updating task:', err);
      throw err instanceof Error ? err : new Error('Failed to update task');
    }
  };

  const deleteTask = async (id: string) => {
    try {
      const { error } = await supabase.functions.invoke('tasks-api', {
        body: {
          action: 'delete',
          id
        }
      });

      if (error) throw error;

      await fetchTasks(); // Refresh list
    } catch (err) {
      console.error('Error deleting task:', err);
      throw err instanceof Error ? err : new Error('Failed to delete task');
    }
  };

  const getTasksByStatus = () => {
    return {
      open: tasks.filter(t => t.status === 'open'),
      in_progress: tasks.filter(t => t.status === 'in_progress'),
      blocked: tasks.filter(t => t.status === 'blocked'),
      done: tasks.filter(t => t.status === 'done')
    };
  };

  return {
    tasks,
    loading,
    error,
    createTask,
    updateTask,
    deleteTask,
    getTasksByStatus,
    refetch: fetchTasks
  };
}